import { useState } from 'react';
import {
  Card,
  LoadingSpinner,
  Stack,
  Tabs,
  VStack,
} from '@adc/parallax-component-library';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { useDepartmentDetails } from '~/api/query/departmentDetails';
import BreadCrumb from '~/components/BreadCrumb';
import { USER_ROLES } from '~/features/auth/roles';
import {
  DepartmentTabsConfig,
  type DepartmentTabsValue,
} from '~/features/department/constants';
import { DepartmentTabContent } from '~/features/department/departmentTabsContent';
import { Route as DepartmentAndSettingsRoute } from '~/routes/hcp-admin/departments.index';
import { buildTestId } from '~/utils/buildTestId';
import { protectedRoute } from '~/utils/routesAuthorization';

const TEST_ID = 'department-details';

export const Route = createFileRoute('/hcp-admin/departments/$departmentId')({
  component: RouteComponent,
  params: {
    parse: (params) => ({ departmentId: params.departmentId }),
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.HcpAdmin, USER_ROLES.NonHcpAdmin],
    });
  },
});

function RouteComponent() {
  const { departmentId } = Route.useParams();
  const { data, isLoading } = useDepartmentDetails({ departmentId });
  const [selectedTab, setSelectedTab] = useState<DepartmentTabsValue>(0);

  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <VStack>
      <BreadCrumb
        testID={TEST_ID}
        sections={['Department & Setting', data?.department ?? departmentId]}
        onPress={() =>
          void navigate({ to: DepartmentAndSettingsRoute.fullPath })
        }
      />

      <Stack marginVertical="$2">
        <Tabs
          tabs={DepartmentTabsConfig}
          selectedIndex={selectedTab}
          testID={buildTestId({ testID: TEST_ID, suffix: 'tabs' })}
          onSelect={(index) => {
            setSelectedTab(index as DepartmentTabsValue);
          }}
        />
      </Stack>

      <VStack marginVertical="$3" maxWidth={820}>
        {isLoading && (
          <Card>
            <Stack
              justifyContent="center"
              alignContent="center"
              alignItems="center"
              flex={1}
              minHeight={300}
            >
              <LoadingSpinner />
            </Stack>
          </Card>
        )}
        {!isLoading && data && (
          <DepartmentTabContent
            activeTab={selectedTab}
            departmentDetails={data}
          />
        )}
      </VStack>
    </VStack>
  );
}
